import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Search, Pencil, Trash2, Eye, Users, Download, X, Save, Loader2, UserRound, FileText,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import FormField from "../components/FormField";
import MastersPanel from "../components/MastersPanel";
import { formSteps } from "../mock";
import { getPartners, updatePartner, deletePartner, getMasters, fileUrl } from "../api";
import { toast } from "../hooks/use-toast";

const BB_LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_parceiros-bb-digital/artifacts/jxz0vlm8_icon-logo-bb-100.png";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "-"; }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [masters, setMasters] = useState([]);
  const [query, setQuery] = useState("");
  const [masterFilter, setMasterFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const refresh = async () => {
    try {
      const [p, m] = await Promise.all([getPartners(), getMasters()]);
      setPartners(p);
      setMasters(m);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("bb_admin_token");
        navigate("/admin");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("bb_admin_token")) {
      navigate("/admin");
      return;
    }
    refresh();
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return partners.filter((p) => {
      const matchesQuery = !q || [p.primeiroNome, p.sobrenome, p.email, p.cpf, p.cidade, p.profissao, p.masterName]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
      const matchesMaster =
        masterFilter === "all" ||
        (masterFilter === "none" && !p.masterCode) ||
        p.masterCode === masterFilter;
      return matchesQuery && matchesMaster;
    });
  }, [partners, query, masterFilter]);

  const logout = () => {
    localStorage.removeItem("bb_admin_token");
    navigate("/admin");
  };

  const openEdit = (p) => { setEditing(p); setEditData({ ...p }); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await updatePartner(editing.id, editData);
      await refresh();
      setEditing(null);
      toast({ title: "Dados atualizados", description: "As alterações foram salvas com sucesso." });
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    await deletePartner(toDelete.id);
    await refresh();
    setToDelete(null);
    toast({ title: "Cadastro removido", description: "O parceiro foi excluído." });
  };

  const exportCSV = () => {
    const headers = ["Nome", "Sobrenome", "Email", "Telefone", "CPF", "Cidade", "Estado", "Profissão", "Cadastro"];
    const rows = partners.map((p) => [
      p.primeiroNome, p.sobrenome, p.email, p.telefone, p.cpf, p.cidade, p.estado, p.profissao, formatDate(p.createdAt),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c || ""}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "parceiros_bb_consorcios.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      {/* Top bar (no logo, per request) */}
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3">
          <span className="font-heading text-sm font-extrabold uppercase tracking-[0.2em] text-[#465EFF]">
            Painel Administrativo
          </span>
          <Button variant="ghost" onClick={logout} className="text-slate-500 hover:text-red-500">
            <LogOut className="mr-2 h-4 w-4" /> Sair
          </Button>
        </div>
      </header>

      <main className="relative mx-auto max-w-7xl px-5 py-8">
        {/* BB logo watermark (user-provided asset) - large & subtle */}
        <img
          src={BB_LOGO}
          alt=""
          aria-hidden="true"
          className="pointer-events-none fixed right-4 top-1/2 -z-0 hidden w-[560px] max-w-[60vw] -translate-y-1/2 opacity-[0.04] md:block"
        />

        <div className="relative z-10">
          <Tabs defaultValue="parceiros">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <TabsList className="bg-white">
                <TabsTrigger value="parceiros" className="data-[state=active]:bg-[#465EFF] data-[state=active]:text-white">
                  <Users className="mr-2 h-4 w-4" /> Parceiros ({partners.length})
                </TabsTrigger>
                <TabsTrigger value="masters" className="data-[state=active]:bg-[#465EFF] data-[state=active]:text-white">
                  <UserRound className="mr-2 h-4 w-4" /> Masters ({masters.length})
                </TabsTrigger>
              </TabsList>
              <Button onClick={exportCSV} variant="outline" className="border-slate-200 text-[#0A1B6B]">
                <Download className="mr-2 h-4 w-4" /> Exportar CSV
              </Button>
            </div>

            <TabsContent value="parceiros" className="mt-0">
              <div className="mb-4">
                <h1 className="font-heading text-2xl font-extrabold text-[#0A1B6B]">Cadastros de Parceiros</h1>
                <p className="text-sm text-slate-500">Gerencie, visualize e edite os dados dos representantes.</p>
              </div>

              <div className="rounded-2xl border border-slate-100 bg-white shadow-sm">
                <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center">
                  <div className="relative flex-1 sm:max-w-sm">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Buscar por nome, e-mail, CPF, cidade..."
                      className="pl-9"
                    />
                  </div>
                  <Select value={masterFilter} onValueChange={setMasterFilter}>
                    <SelectTrigger className="w-full bg-white sm:w-56">
                      <SelectValue placeholder="Filtrar por master" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os masters</SelectItem>
                      <SelectItem value="none">Sem master (direto)</SelectItem>
                      {masters.map((m) => (
                        <SelectItem key={m.id} value={m.code}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Parceiro</TableHead>
                  <TableHead>Contato</TableHead>
                  <TableHead>Master</TableHead>
                  <TableHead className="hidden md:table-cell">Localização</TableHead>
                  <TableHead className="hidden lg:table-cell">Profissão</TableHead>
                  <TableHead className="hidden sm:table-cell">Cadastro</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-12 text-center text-slate-400">
                      Nenhum parceiro encontrado.
                    </TableCell>
                  </TableRow>
                ) : filtered.map((p) => (
                  <TableRow key={p.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.foto?.url ? (
                          <img src={fileUrl(p.foto.url)} alt="" className="h-9 w-9 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FCFC30] font-heading text-sm font-bold text-[#0A1B6B]">
                            {(p.primeiroNome?.[0] || "?").toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-semibold text-[#0A1B6B]">{p.primeiroNome} {p.sobrenome}</div>
                          <div className="text-xs text-slate-400">{p.cpf}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-slate-600">{p.email}</div>
                      <div className="text-xs text-slate-400">{p.whatsapp || p.telefone}</div>
                    </TableCell>
                    <TableCell>
                      {p.masterName ? (
                        <Badge className="bg-[#FCFC30] font-medium text-[#0A1B6B] hover:bg-[#FCFC30]">{p.masterName}</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Direto</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm text-slate-600">{p.cidade}</span>
                      {p.estado && <Badge variant="secondary" className="ml-2 bg-[#465EFF]/10 text-[#465EFF] hover:bg-[#465EFF]/10">{p.estado}</Badge>}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-600">{p.profissao || "-"}</TableCell>
                    <TableCell className="hidden sm:table-cell text-sm text-slate-500">{formatDate(p.createdAt)}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setViewing(p)} className="h-8 w-8 text-slate-400 hover:text-[#465EFF]">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(p)} className="h-8 w-8 text-slate-400 hover:text-[#465EFF]">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setToDelete(p)} className="h-8 w-8 text-slate-400 hover:text-red-500">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
              </div>
            </TabsContent>

            <TabsContent value="masters" className="mt-0">
              <div className="mb-4">
                <h1 className="font-heading text-2xl font-extrabold text-[#0A1B6B]">Masters</h1>
                <p className="text-sm text-slate-500">Cadastre seus masters e gere o link exclusivo de cada um.</p>
              </div>
              <MastersPanel onChanged={refresh} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* View details dialog */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0A1B6B]">
              {viewing?.primeiroNome} {viewing?.sobrenome}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {viewing.foto?.url && (
                  <img src={fileUrl(viewing.foto.url)} alt="" className="h-20 w-20 rounded-xl object-cover ring-2 ring-[#465EFF]/20" />
                )}
                <div className="rounded-lg bg-slate-50 px-4 py-2">
                  <span className="text-xs text-slate-400">Master responsável</span>
                  <div className="text-sm font-semibold text-[#0A1B6B]">
                    {viewing.masterName || "Cadastro direto (sem master)"}
                  </div>
                </div>
              </div>
              {formSteps.map((s) => (
                <div key={s.title}>
                  <h4 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-[#465EFF]">{s.title}</h4>
                  <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    {s.fields.map((f) => (
                      <div key={f.name} className="flex flex-col border-b border-slate-100 py-1.5">
                        <span className="text-xs text-slate-400">{f.label}</span>
                        {f.type === "file" ? (
                          viewing[f.name]?.url ? (
                            <a
                              href={fileUrl(viewing[f.name].url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm font-medium text-[#465EFF] hover:underline"
                            >
                              <FileText className="h-3.5 w-3.5" /> {viewing[f.name].name || "Ver documento"}
                            </a>
                          ) : (
                            <span className="text-sm text-slate-400">Não enviado</span>
                          )
                        ) : (
                          <span className="text-sm text-slate-700">
                            {f.type === "checkbox" ? (viewing[f.name] ? "Sim" : "Não") : (viewing[f.name] || "-")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#0A1B6B]">Editar Parceiro</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {formSteps.map((s) => (
              <div key={s.title}>
                <h4 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-[#465EFF]">{s.title}</h4>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {s.fields.map((f) => (
                    <FormField
                      key={f.name}
                      field={f}
                      value={editData[f.name]}
                      onChange={(v) => setEditData((d) => ({ ...d, [f.name]: v }))}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              <X className="mr-2 h-4 w-4" /> Cancelar
            </Button>
            <Button onClick={saveEdit} disabled={saving} className="bg-[#465EFF] text-white hover:bg-[#3448d6]">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cadastro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação removerá permanentemente o cadastro de {toDelete?.primeiroNome} {toDelete?.sobrenome}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
